/**
 * PNG 角色卡读写工具
 * 手动解析 PNG 文件格式，读写 tEXt 块中的角色卡数据
 */

const PNG_SIGNATURE = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
const CHUNK_TYPE_TEXT = new Uint8Array([0x74, 0x45, 0x58, 0x74]) // 'tEXt'

function arrayEquals(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

function readUint32(view: DataView, offset: number): number {
  return view.getUint32(offset, false) // big-endian
}

function writeUint32(value: number): Uint8Array {
  const arr = new Uint8Array(4)
  new DataView(arr.buffer).setUint32(0, value, false)
  return arr
}

function crc32(data: Uint8Array): number {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[i] = c
  }

  let crc = ~0
  for (let i = 0; i < data.length; i++) {
    crc = table[(crc ^ data[i]) & 0xff] ^ (crc >>> 8)
  }
  return ~crc >>> 0
}

function concatArrays(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, arr) => sum + arr.length, 0)
  const result = new Uint8Array(totalLength)
  let offset = 0
  for (const arr of arrays) {
    result.set(arr, offset)
    offset += arr.length
  }
  return result
}

/**
 * UTF-8 safe bytes to string conversion
 */
function bytesToString(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes)
}

/**
 * UTF-8 safe string to bytes conversion
 */
function stringToBytes(str: string): Uint8Array {
  return new TextEncoder().encode(str)
}

/**
 * Base64 string to Uint8Array (UTF-8 safe, replaces atob)
 */
function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64)
  const len = binaryString.length
  const bytes = new Uint8Array(len)
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes
}

/**
 * Uint8Array to Base64 string (UTF-8 safe, replaces btoa)
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binaryString = ''
  const len = bytes.length
  for (let i = 0; i < len; i++) {
    binaryString += String.fromCharCode(bytes[i])
  }
  return btoa(binaryString)
}

interface PngChunk {
  length: number
  type: Uint8Array
  data: Uint8Array
  crc: number
}

function parsePngChunks(arrayBuffer: ArrayBuffer): PngChunk[] {
  const view = new DataView(arrayBuffer)
  const bytes = new Uint8Array(arrayBuffer)
  let offset = PNG_SIGNATURE.length
  const chunks: PngChunk[] = []

  while (offset < bytes.length) {
    const length = readUint32(view, offset)
    const typeStart = offset + 4
    const dataStart = offset + 8
    const crcStart = dataStart + length

    if (crcStart + 4 > bytes.length) {
      throw new Error('PNG chunk truncated')
    }

    const type = bytes.slice(typeStart, typeStart + 4)
    const data = bytes.slice(dataStart, dataStart + length)
    const crc = readUint32(view, crcStart)

    chunks.push({ length, type, data, crc })

    offset = crcStart + 4
  }

  return chunks
}

function serializePngChunks(chunks: PngChunk[]): Uint8Array {
  const parts: Uint8Array[] = [PNG_SIGNATURE]

  for (const chunk of chunks) {
    const typeAndData = concatArrays(chunk.type, chunk.data)
    const computedCrc = crc32(typeAndData)

    parts.push(writeUint32(chunk.length))
    parts.push(chunk.type)
    parts.push(chunk.data)
    parts.push(writeUint32(computedCrc))
  }

  return concatArrays(...parts)
}

function findTextChunk(chunks: PngChunk[], key: string): { chunk: PngChunk; index: number } | null {
  const keyBytes = stringToBytes(key)

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i]
    if (!arrayEquals(chunk.type, CHUNK_TYPE_TEXT)) continue

    // tEXt format: keyword\0text
    const nullIndex = chunk.data.indexOf(0)
    if (nullIndex === -1) continue

    const chunkKey = chunk.data.slice(0, nullIndex)
    if (arrayEquals(chunkKey, keyBytes)) {
      return { chunk, index: i }
    }
  }

  return null
}

function createTextChunk(key: string, text: string): PngChunk {
  const keyBytes = stringToBytes(key)
  const textBytes = stringToBytes(text)
  const data = concatArrays(keyBytes, new Uint8Array([0]), textBytes)

  return {
    length: data.length,
    type: new Uint8Array(CHUNK_TYPE_TEXT),
    data,
    crc: 0, // will be computed during serialization
  }
}

/**
 * 从 PNG 文件的 tEXt 块中提取角色卡数据
 * @param arrayBuffer PNG 文件的 ArrayBuffer
 * @returns 解析后的 JSON 数据
 */
export async function extractCardFromPng(arrayBuffer: ArrayBuffer): Promise<any> {
  const chunks = parsePngChunks(arrayBuffer)
  const found = findTextChunk(chunks, 'chara')

  if (!found) {
    throw new Error('PNG 中未找到角色卡数据（tEXt chunk key: chara）')
  }

  const { chunk } = found
  const nullIndex = chunk.data.indexOf(0)
  const textBytes = chunk.data.slice(nullIndex + 1)
  const base64Text = bytesToString(textBytes)

  try {
    const jsonBytes = base64ToUint8Array(base64Text)
    const jsonText = bytesToString(jsonBytes)
    return JSON.parse(jsonText)
  } catch (e) {
    throw new Error(`解析角色卡数据失败: ${e instanceof Error ? e.message : String(e)}`)
  }
}

/**
 * 将角色卡数据嵌入 PNG 文件的 tEXt 块
 * @param arrayBuffer PNG 文件的 ArrayBuffer
 * @param cardData 角色卡 JSON 数据
 * @returns 包含嵌入数据的 Blob
 */
export async function embedCardIntoPng(arrayBuffer: ArrayBuffer, cardData: any): Promise<Blob> {
  const chunks = parsePngChunks(arrayBuffer)

  const jsonText = JSON.stringify(cardData)
  const jsonBytes = stringToBytes(jsonText)
  const base64Text = uint8ArrayToBase64(jsonBytes)
  const newChunk = createTextChunk('chara', base64Text)

  const found = findTextChunk(chunks, 'chara')
  if (found) {
    // 替换已有的 chara chunk
    chunks[found.index] = newChunk
  } else {
    // 在 IHDR 后插入新的 chara chunk
    chunks.splice(1, 0, newChunk)
  }

  const result = serializePngChunks(chunks)
  return new Blob([result.buffer as ArrayBuffer], { type: 'image/png' })
}
